import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { CircularProgress, Typography, Paper, Drawer, Divider } from '@mui/material';
import AdminDataGrid from '../AdminDataGrid';

const APPROVALS_API_ROOT = String(process.env.REACT_APP_API_URL || "/api").replace(new RegExp("/$"), "");
const APPROVALS_API_BASE = APPROVALS_API_ROOT + "/admin/transcript-approvals";

const ApprovalsTab = ({ isLight }) => {
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [retentionEvents, setRetentionEvents] = useState([]);

  const bg = isLight ? "#ffffff" : "#0f172a";
  const cardBg = isLight ? "#f8fafc" : "#1e293b";
  const textMain = isLight ? "#0f172a" : "#f8fafc";
  const textMuted = isLight ? "#64748b" : "#94a3b8";
  const border = isLight ? "#e2e8f0" : "#334155";

  const apiFetch = useCallback(async (url, opts = {}) => {
    const headers = { "Content-Type": "application/json", ...opts.headers };
    const token = localStorage.getItem("avaada_token");
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(url, { ...opts, headers, credentials: "include" });
    if (!res.ok) throw new Error(await res.text().then(t => t || res.statusText));
    return res.json();
  }, []);

  const loadApprovals = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch(APPROVALS_API_BASE);
      const retention = await apiFetch(APPROVALS_API_ROOT + '/admin/transcript-retention-events');
      setRetentionEvents(Array.isArray(retention) ? retention : (retention?.rows || []));
      const arr = Array.isArray(data) ? data : (data && Array.isArray(data.rows) ? data.rows : []);
      setApprovals(arr);
    } catch (e) {
      console.warn("[approvals] load failed:", e.message);
      setError(e.message || "Failed to load approvals");
      setApprovals([]);
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    loadApprovals();
  }, [loadApprovals]);

  const openApproval = async (approval) => {
    setSelected(approval); setDetail(null); setDetailLoading(true);
    try { const data = await apiFetch(APPROVALS_API_ROOT + `/admin/meetings/${encodeURIComponent(approval.meeting_id)}/transcript`); setDetail(data?.meeting || data); }
    catch (e) { setDetail({ error: e.message }); }
    finally { setDetailLoading(false); }
  };
  const recipientLog = (value) => { if (Array.isArray(value)) return value; if (typeof value === 'string') { try { const parsed = JSON.parse(value); return Array.isArray(parsed) ? parsed : []; } catch (_) {} } return []; };
  const participantStatus = (person, meeting) => { const email = String(person.email || '').trim().toLowerCase(); const log = recipientLog(meeting?.approvalSummary?.distributionEmailLog || meeting?.approvalAccess?.distributionEmailLog || meeting?.distribution_email_log || meeting?.email_recipients_log || meeting?.emailRecipientsLog); const sent = log.find((item) => String(item.email || item.recipient || '').trim().toLowerCase() === email); return { joined: Boolean(person.recall_participant_id), sent: sent ? String(sent.outcome || sent.status || 'sent') + (sent.error ? `: ${sent.error}` : '') : 'not sent' }; };

  const statusColor = useCallback((s) => {
    switch ((s || "").toLowerCase()) {
      case "approved": return "#15803d";
      case "pending": return "#d97706";
      case "expired": return "#dc2626";
      case "superseded": return "#64748b";
      default: return textMuted;
    }
  }, [textMuted]);

  const columns = useMemo(() => [
    { field: 'meeting_title', headerName: 'Meeting', minWidth: 220, flex: 1.25, valueGetter: (_value, row) => row.meeting_title || 'Untitled meeting', renderCell: (params) => <strong style={{ fontSize: 12, color: textMain }}>{params.row.meeting_title || 'Untitled meeting'}</strong> },
    { field: 'approver_email', headerName: 'Approver email', minWidth: 190, flex: 1.1 },
    { field: 'approver_name', headerName: 'Approver', minWidth: 150, flex: 0.9, valueGetter: (_value, row) => row.approver_name || '—' },
    { field: 'authority', headerName: 'Authority', minWidth: 140, flex: 0.8, valueGetter: (_value, row) => row.authority || '—' },
    { field: 'status', headerName: 'Status', minWidth: 110, flex: 0.65, renderCell: (params) => <strong style={{ color: statusColor(params.row.status), fontSize: 12, textTransform: 'capitalize' }}>{params.row.status || '—'}</strong> },
    { field: 'created_at', headerName: 'Created', minWidth: 175, flex: 0.95, valueGetter: (_value, row) => row.created_at ? new Date(row.created_at).toLocaleString() : '—' },
    { field: 'approved_at', headerName: 'Available at', minWidth: 175, flex: 0.95, valueGetter: (_value, row) => row.approved_at ? new Date(row.approved_at).toLocaleString() : '—' },
    { field: 'edited_summary', headerName: 'Edited summary', minWidth: 220, flex: 1.2, valueGetter: (_value, row) => row.edited_summary || '—', renderCell: (params) => <span title={params.row.edited_summary || ''} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{params.row.edited_summary || '—'}</span> },
  ], [textMain, statusColor]);

  if (loading) {
    return (
      <Paper style={{ background: cardBg, border: `1px solid ${border}`, borderRadius: 12, color: textMain, padding: 24 }}>
        <Typography variant="body2" style={{ color: textMuted, textAlign: "center", padding: "40px 0" }}>
          <CircularProgress size={30} /> Loading approvals…
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      style={{
        background: cardBg,
        border: `1px solid ${border}`,
        borderRadius: 12,
        color: textMain,
        padding: 24,
        marginBottom: 20,
      }}
    >
      <Typography variant="h6" component="h2" style={{ marginBottom: 16, color: textMain }}>
        Transcript Approvals — Audit Trail
      </Typography>
      <Typography variant="body2" style={{ color: textMuted, marginBottom: 16 }}>
        Every approval action: who approved, who edited, which meeting, and when.
      </Typography>

      {error && (
        <Typography variant="body2" style={{ color: "#dc2626", marginBottom: 16 }}>
          Error: {error}
        </Typography>
      )}

      <AdminDataGrid
        rows={approvals}
        columns={columns}
        isLight={isLight}
        height="min(56vh, 560px)"
        pageSize={10}
        pageSizeOptions={[5, 10, 25, 50]}
        getRowId={(row) => `${row.meeting_id}-${row.approver_email}`}
        onRowClick={(params) => openApproval(params.row)}
        emptyMessage="No approval records found."
      />

      <Drawer anchor="right" open={Boolean(selected)} onClose={() => setSelected(null)}>
        <div style={{ width: "min(620px, 96vw)", maxWidth: "100%", height: "100vh", overflowY: "auto", padding: 24, boxSizing: "border-box", color: textMain, background: bg }}>
          <Typography variant="h6">Approval details</Typography>
          {detailLoading ? <div style={{ padding: 32, textAlign: 'center' }}><CircularProgress size={26} /></div> : selected && <>
            <Typography variant="body2" style={{ marginTop: 12, color: textMuted }}>Meeting</Typography><Typography variant="subtitle1">{detail?.title || selected.meeting_id}</Typography><Divider style={{ margin: '14px 0' }} />
            {[['Selected approver', selected.approver_name || selected.approver_email], ['Approver email', selected.approver_email], ['Authority / designation', selected.authority], ['Approval status', selected.status], ['Approval email', selected.approval_email_status || 'not sent'], ['Approval email sent time', selected.approval_email_sent_at ? new Date(selected.approval_email_sent_at).toLocaleString() : '—'], ['Created time', selected.created_at ? new Date(selected.created_at).toLocaleString() : '—'], ['Approved time', selected.approved_at ? new Date(selected.approved_at).toLocaleString() : '—']].map(([label, value]) => <div key={label} style={{ marginBottom: 10 }}><Typography variant="caption" style={{ color: textMuted }}>{label}</Typography><div>{value || '—'}</div></div>)}
            <Typography variant="caption" style={{ color: textMuted }}>Edited summary</Typography><div style={{ whiteSpace: 'pre-wrap', margin: '4px 0 16px' }}>{selected.edited_summary || selected.original_summary || '—'}</div>
            {detail?.error ? <Typography color="error">{detail.error}</Typography> : <><Typography variant="subtitle2" style={{ margin: '12px 0 8px' }}>Participants and email delivery</Typography>{(detail?.participants || []).filter((p) => !p.is_bot).map((person) => { const state = participantStatus(person, detail); return <div key={person.id || person.email} style={{ padding: '8px 0', borderBottom: `1px solid ${border}` }}><div>{person.name || 'Unknown'} <span style={{ color: textMuted }}>({person.email})</span></div><div style={{ fontSize: 12, color: textMuted }}>{state.joined ? 'Joined' : 'Did not join'} · Email {state.sent}</div></div>; })}{!(detail?.participants || []).filter((p) => !p.is_bot).length && <Typography variant="body2">No participant records found.</Typography>}</>}
          </>}
        </div>
      </Drawer>

      <div style={{ marginTop: 24, borderTop: `1px solid ${border}`, paddingTop: 16 }}>
        <Typography variant="subtitle1" style={{ color: textMain }}>Retention audit log</Typography>
        <Typography variant="body2" style={{ color: textMuted, marginBottom: 10 }}>Reminder and transcript-deletion events are preserved here after approval records expire.</Typography>
        {retentionEvents.length ? retentionEvents.map((event) => <div key={event.id} style={{ padding: '8px 0', borderBottom: `1px solid ${border}`, fontSize: 12 }}><strong>{event.event_type === 'transcript_purged' ? 'Transcript deleted' : 'Approval reminder'} · {event.event_status || 'recorded'}</strong> · Meeting {event.meeting_id} · {event.approver_email || '—'} · {event.created_at ? new Date(event.created_at).toLocaleString() : '—'}</div>) : <Typography variant="body2" style={{ color: textMuted }}>No retention events recorded.</Typography>}
      </div>

      <div style={{ marginTop: 16, color: textMuted, fontSize: 12, lineHeight: 1.5 }}>
        <p>• <strong>Approved</strong> — first approver won, transcript released.</p>
        <p>• <strong>Pending</strong> — waiting for an approver to act.</p>
        <p>• <strong>Expired</strong> — 15 days passed without approval; transcripts deleted.</p>
        <p>• <strong>Superseded</strong> — another approver already approved this meeting.</p>
      </div>
    </Paper>
  );
};

export default ApprovalsTab;
